import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Popover,
  SegmentedControl,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import {
  useInvestigationInbox,
  useInvestigationList,
} from "../../hooks/useInvestigations";
import useCurrentUser from "../../hooks/useCurrentUser";
import { InvestigationBulkActions } from "./InvestigationBulkActions";
import { InvestigationTable, toRows } from "./InvestigationTable";
import { useTakeOwnership } from "./useTakeOwnership";
import {
  REGISTRY_PRESETS,
  attentionInbox,
  attentionItems,
  matchingPreset,
  snapshotTimestampToDate,
} from "./investigationUtils";
import { formatWhen } from "./formatWhen";
import { GroupsFilterQuery } from "./groupsFilter";

/**
 * The registry pages: the live active table (every active case, whoever holds it — "mine" and
 * "needs an owner" are filter presets on it, not pages) and the resolved archive.
 */
export type RegistrySection = "active" | "resolved" | "cancelled" | "all";

interface InvestigationWorkQueueProps {
  stage: Stage;
  section: RegistrySection;
  onOpenInvestigation: (investigationId: string) => void;
  /**
   * The table's filter, when the host carries it (page URL / sidebar presets); the "Show mine"
   * toggle and the filter bar report through onFilterChange. See InvestigationTable.
   */
  filter?: GroupsFilterQuery;
  onFilterChange?: (filter: GroupsFilterQuery | undefined) => void;
  /** The page header already names the section: the table is just "Cases (n)". */
  pageTitled?: boolean;
  /** Host control beside the filter bar (e.g. Resolved | Cancelled); Active uses All | Mine. */
  filterControl?: React.ReactNode;
}

const MINE_PRESET = REGISTRY_PRESETS[0];

/** Pages auto-loaded so the table can sort and filter the whole view client-side. */
export const MAX_AUTO_LOADED_PAGES = 10;

interface MineSwitchProps {
  cases: number;
  attention: number;
  onlyMine: boolean;
  onToggle: (onlyMine: boolean) => void;
}

/**
 * "All | Mine (n)" beside the filter bar (PropertyFilter customControl): the Mine preset as a
 * switch. A warning icon on Mine means some of the viewer's cases need their attention.
 */
export const MineSwitch: React.FC<MineSwitchProps> = ({
  cases,
  attention,
  onlyMine,
  onToggle,
}) => (
  <div data-testid="mine-switch">
    <SegmentedControl
      label="Whose investigations"
      selectedId={onlyMine ? "mine" : "all"}
      onChange={({ detail }) => onToggle(detail.selectedId === "mine")}
      options={[
        { id: "all", text: "All" },
        {
          id: "mine",
          text: `Mine (${cases})`,
          ...(attention > 0 && {
            iconName: "status-warning" as const,
            iconAlt: `${attention} need${
              attention === 1 ? "s" : ""
            } your attention`,
          }),
        },
      ]}
    />
  </div>
);

const SECTION_COPY: Record<RegistrySection, { title: string; empty: string }> =
  {
    active: {
      title: "Active investigations",
      empty: "No active investigations.",
    },
    resolved: {
      title: "Resolved investigations",
      empty: "No resolved investigations yet.",
    },
    cancelled: {
      title: "Cancelled investigations",
      empty: "No cancelled investigations.",
    },
    all: {
      title: "All investigations",
      empty: "No investigations yet.",
    },
  };

type InvestigationList = ReturnType<typeof useInvestigationList>;

/**
 * Every case, whatever its status: the active, resolved and cancelled views read together (the
 * API's index walks are per status), merged into one list. Only enabled lists are combined, since
 * a disabled query reports isLoading forever.
 */
function useRegistryList(stage: Stage, section: RegistrySection) {
  const all = section === "all";
  // The API still calls the cancelled slice by its status name, WITHDRAWN; "all" starts with
  // the active list and adds the closed ones below. view=withdrawn also returns merged sources
  // and auto-closed duplicates (RESOLVED on the wire; the backend chains a second walk for
  // them), which view=resolved leaves out, so together the three lists cover every case.
  const PRIMARY_VIEW: Record<RegistrySection, string> = {
    active: "active",
    resolved: "resolved",
    cancelled: "withdrawn",
    all: "active",
  };
  const primary = useInvestigationList(stage, {
    view: PRIMARY_VIEW[section],
  });
  const resolved = useInvestigationList(stage, { view: "resolved" }, all);
  const cancelled = useInvestigationList(stage, { view: "withdrawn" }, all);
  if (!all) return { ...primary, partialError: null };
  const lists: InvestigationList[] = [primary, resolved, cancelled];
  const seen = new Set<string>();
  return {
    data: primary.data,
    // Only the active list blanks the page; a failed closed list still shows what loaded, with
    // a warning (partialError) so the table is not silently incomplete.
    error: primary.error,
    partialError: lists.find((l) => l.error)?.error ?? null,
    isLoading: lists.some((l) => l.isLoading),
    items: lists
      .flatMap((l) => l.items)
      .filter((inv) => !seen.has(inv.id) && !!seen.add(inv.id)),
    hasMore: lists.some((l) => l.hasMore),
    // One page at a time, from the list with the fewest loaded cases that still has more, so a
    // long active list cannot use up the auto-load budget before any closed case is loaded.
    loadMore: () =>
      lists
        .filter((l) => l.hasMore)
        .sort((a, b) => a.items.length - b.items.length)[0]
        ?.loadMore() ?? Promise.resolve(),
    reset: () => Promise.all(lists.map((l) => l.reset())),
  };
}

/**
 * A registry page: the full, filterable, sortable table of the active investigations (with Take
 * ownership on ownerless rows), of the resolved ones or of the cancelled ones. Remaining pages are auto-loaded so
 * sorting and the PropertyFilter cover the whole slice. "Mine" and "Needs an owner" are filter
 * presets over the active table, so a slice is just the same table with tokens in the bar (and
 * in the URL), and any further filter the user adds stays put when switching between them.
 */
export const InvestigationWorkQueue: React.FC<InvestigationWorkQueueProps> = ({
  stage,
  section,
  onOpenInvestigation,
  filter,
  onFilterChange,
  pageTitled = false,
  filterControl: hostFilterControl,
}) => {
  const currentUser = useCurrentUser();
  const list = useRegistryList(stage, section);
  const [autoLoadedPages, setAutoLoadedPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const reload = () => {
    setAutoLoadedPages(0);
    return list.reset();
  };
  const { takeOwnership, claimingId, justClaimedIds, toasts } =
    useTakeOwnership(stage, reload);

  useEffect(() => {
    setAutoLoadedPages(0);
    setSelectedIds(new Set());
  }, [stage, section]);
  const { hasMore, loadMore, isLoading } = list;
  // "All" reads three lists, so it gets three lists' worth of auto-loaded pages.
  const maxAutoLoaded =
    section === "all" ? MAX_AUTO_LOADED_PAGES * 3 : MAX_AUTO_LOADED_PAGES;
  useEffect(() => {
    if (
      isLoading ||
      loadingMore ||
      !hasMore ||
      autoLoadedPages >= maxAutoLoaded
    )
      return;
    setLoadingMore(true);
    loadMore()
      .then(() => setAutoLoadedPages((n) => n + 1))
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [
    isLoading,
    loadingMore,
    hasMore,
    autoLoadedPages,
    loadMore,
    maxAutoLoaded,
  ]);

  const { items } = list;
  const tableLoading = list.isLoading || loadingMore;
  const copy = SECTION_COPY[section];

  // Bulk actions act on active cases only (closed ones are locked); the selection is kept by
  // id and narrowed to rows still in this view, so a case that left after a refetch drops out.
  const selectable = section === "active";
  const selected = selectable
    ? items.filter((inv) => selectedIds.has(inv.id))
    : [];

  // The bell's feed (same query, no extra request), so the flag clears when it is read.
  const inbox = attentionInbox(useInvestigationInbox()?.data?.items);
  const rows = toRows(list.items, currentUser, inbox);
  const mineRows = rows.filter((r) => r.ownership === "Mine");
  const attention = attentionItems(list.items, currentUser, inbox).length;

  const snapshotLabel = list.data?.snapshotTimestamp
    ? `DLQ snapshot ${formatWhen(
        snapshotTimestampToDate(list.data.snapshotTimestamp)
      )}`
    : undefined;
  // The snapshot time is secondary: click "Updated…" to see it (Cloudscape text popover).
  const updatedLabel = list.data?.registryTimestamp
    ? `Updated ${formatWhen(new Date(list.data.registryTimestamp))}`
    : "Live";
  const provenance = snapshotLabel ? (
    <Popover
      triggerType="text"
      size="small"
      position="top"
      dismissButton={false}
      content={snapshotLabel}
    >
      {updatedLabel}
    </Popover>
  ) : (
    <span>{updatedLabel}</span>
  );

  if (list.error) {
    return (
      <Alert type="error" header="Investigation service unavailable">
        {list.error.message}. The DLQ dashboard keeps working; investigation
        state is temporarily unavailable.
      </Alert>
    );
  }

  // "Mine" is the Mine preset; "All" clears it (and only it).
  const onlyMine = matchingPreset(REGISTRY_PRESETS, filter)?.id === "mine";
  const switchable = section === "active" && !!onFilterChange;

  return (
    <SpaceBetween size="m">
      {toasts.element}
      {list.partialError && (
        <Alert type="warning">
          Some closed cases could not be loaded ({list.partialError.message}).
          The list may be incomplete.
        </Alert>
      )}
      <InvestigationTable
        stage={stage}
        title={pageTitled ? "Cases" : copy.title}
        filterControl={
          switchable && !!currentUser ? (
            <MineSwitch
              cases={mineRows.length}
              attention={attention}
              onlyMine={onlyMine}
              onToggle={(next) =>
                onFilterChange?.(next ? MINE_PRESET.filter : undefined)
              }
            />
          ) : (
            hostFilterControl
          )
        }
        emptyText={copy.empty}
        hideTotals={section === "cancelled"}
        items={items}
        currentUser={currentUser}
        attention={inbox}
        highlightIds={section === "active" ? justClaimedIds : undefined}
        filter={filter}
        onFilterChange={onFilterChange}
        loading={tableLoading}
        hasMore={list.hasMore && !loadingMore}
        onLoadMore={() => {
          // Manual continuation once the auto-load budget is spent.
          setAutoLoadedPages(0);
        }}
        onOpen={onOpenInvestigation}
        selectedIds={selectable ? selectedIds : undefined}
        onSelectedIdsChange={
          selectable ? (ids) => setSelectedIds(new Set(ids)) : undefined
        }
        headerActions={
          // Cloudscape puts "last updated" beside the header actions, not in the description.
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Box color="text-body-secondary" fontSize="body-s">
              {provenance}
            </Box>
            {selectable && !!currentUser && (
              <InvestigationBulkActions
                stage={stage}
                selected={selected}
                currentUser={currentUser}
                toasts={toasts}
                onDone={() => {
                  setSelectedIds(new Set());
                  return reload();
                }}
              />
            )}
          </SpaceBetween>
        }
        onTakeOwnership={section === "active" ? takeOwnership : undefined}
        takingOwnershipId={claimingId}
      />
    </SpaceBetween>
  );
};

export default InvestigationWorkQueue;
